'use client';

import React, { useState, useMemo } from 'react';
import { Check, Library, Loader2, Pencil, Plus, Search, Trash2, X } from 'lucide-react';
import {
  createSubjectMaster, deleteSubjectMaster, updateSubjectMaster,
  type Subject,
} from '@/actions/data-fetch-curriculum';
import { toastMessage } from '@/utils/toast-message';
import {
  SUBJECT_CATALOG, GRADE_GROUP_LABELS, CATEGORY_LABELS, CATEGORY_CAT_CLASS,
  type GradeGroup, type SubjectCategory,
} from './subjectCatalog';

const CATS_ORDER: (SubjectCategory | 'other')[] = ['language', 'core', 'social', 'activity', 'skill', 'sports', 'elective', 'other'];

export const SubjectLibrary: React.FC<{
  subjects: Subject[];
  onRefresh: () => void;
}> = ({ subjects, onRefresh }) => {
  const [showCatalog, setShowCatalog] = useState(false);
  const [catalogGroup, setCatalogGroup] = useState<GradeGroup | 'all'>('all');
  const [catalogSearch, setCatalogSearch] = useState('');
  const [catalogSelected, setCatalogSelected] = useState<Set<string>>(new Set());
  const [saving, setSaving] = useState(false);
  const [listSearch, setListSearch] = useState('');
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editName, setEditName] = useState('');
  const [editCode, setEditCode] = useState('');
  const [editSaving, setEditSaving] = useState(false);

  const existingNames = useMemo(() => new Set(subjects.map(s => s.name.toLowerCase())), [subjects]);

  const catalogFiltered = useMemo(() => SUBJECT_CATALOG.filter(e => {
    const matchGroup = catalogGroup === 'all' || e.gradeGroups.includes(catalogGroup as GradeGroup);
    const matchSearch = !catalogSearch || e.name.toLowerCase().includes(catalogSearch.toLowerCase());
    return matchGroup && matchSearch;
  }), [catalogGroup, catalogSearch]);

  const catalogGrouped = useMemo(() => {
    const groups: Partial<Record<SubjectCategory, typeof SUBJECT_CATALOG>> = {};
    for (const e of catalogFiltered) {
      if (!groups[e.category]) groups[e.category] = [];
      groups[e.category]!.push(e);
    }
    return groups;
  }, [catalogFiltered]);

  const listFiltered = useMemo(() => subjects.filter(s =>
    !listSearch || s.name.toLowerCase().includes(listSearch.toLowerCase())
  ), [subjects, listSearch]);

  const byCategory = useMemo(() => {
    const map: Partial<Record<string, Subject[]>> = {};
    for (const s of listFiltered) {
      const cat = s.category ?? 'other';
      if (!map[cat]) map[cat] = [];
      map[cat]!.push(s);
    }
    return map;
  }, [listFiltered]);

  const closeCatalog = () => { setShowCatalog(false); setCatalogSelected(new Set()); setCatalogSearch(''); setCatalogGroup('all'); };

  const handleAddFromCatalog = async () => {
    if (catalogSelected.size === 0) return;
    setSaving(true);
    const toAdd = SUBJECT_CATALOG.filter(e => catalogSelected.has(e.name));
    let added = 0;
    for (const entry of toAdd) {
      try {
        const res = await createSubjectMaster({ name: entry.name, category: entry.category });
        if (res?.success) added++;
      } catch { /* duplicate — skip */ }
    }
    setSaving(false);
    if (added > 0) { toastMessage(`Added ${added} subject${added > 1 ? 's' : ''}`, 'success'); onRefresh(); }
    closeCatalog();
  };

  const handleUpdate = async (id: number) => {
    if (!editName.trim()) return;
    setEditSaving(true);
    try {
      const res = await updateSubjectMaster(id, { name: editName.trim(), code: editCode.trim() || undefined });
      if (res?.success) { toastMessage('Subject updated', 'success'); setEditingId(null); onRefresh(); }
      else toastMessage((res as any)?.message || 'Failed', 'error');
    } finally { setEditSaving(false); }
  };

  const handleDelete = async (subject: Subject) => {
    if (!confirm(`Delete "${subject.name}"?`)) return;
    const res = await deleteSubjectMaster(subject.id);
    if (res?.success) { toastMessage('Subject deleted', 'success'); onRefresh(); }
    else toastMessage((res as any)?.message || 'Failed to delete', 'error');
  };

  return (
    <div style={{ padding: '24px 28px', maxWidth: 860 }}>
      {/* Header row */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
        <div>
          <div style={{ fontWeight: 700, fontSize: 15, color: 'var(--text)' }}>Subject Library</div>
          <p className="meta" style={{ marginTop: 2 }}>
            {subjects.length} subject{subjects.length !== 1 ? 's' : ''} in your school catalog
          </p>
        </div>
        <button className="btn btn-primary btn-sm" onClick={() => setShowCatalog(true)}>
          <Library size={14} /> Browse Catalog
        </button>
      </div>

      {/* Search */}
      {subjects.length > 6 && (
        <div style={{ position: 'relative', marginBottom: 16 }}>
          <Search size={14} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-3)', pointerEvents: 'none' }} />
          <input value={listSearch} onChange={e => setListSearch(e.target.value)} placeholder="Search subjects…"
            className="input" style={{ paddingLeft: 36, width: '100%' }} />
        </div>
      )}

      {/* Subject list */}
      {subjects.length === 0 ? (
        <div className="empty" style={{ padding: '56px 24px' }}>
          <div className="empty-art"><Library size={40} /></div>
          <h3>No subjects yet</h3>
          <p>Browse the catalog to add subjects to your school library</p>
          <button className="btn btn-primary" style={{ marginTop: 12 }} onClick={() => setShowCatalog(true)}>
            <Library size={15} /> Browse Catalog
          </button>
        </div>
      ) : listFiltered.length === 0 ? (
        <div className="empty" style={{ padding: 32 }}><p>No subjects match "{listSearch}"</p></div>
      ) : (
        <div className="card" style={{ overflow: 'hidden' }}>
          {CATS_ORDER.filter(cat => byCategory[cat]?.length).map(cat => (
            <div key={cat}>
              <div style={{ padding: '7px 16px', background: 'var(--surface-2)', borderBottom: '1px solid var(--divider)', fontSize: 11, fontWeight: 800, letterSpacing: 0.6, textTransform: 'uppercase', color: 'var(--text-3)' }}>
                {CATEGORY_LABELS[cat as SubjectCategory] ?? 'Other'}
              </div>
              <table className="tbl" style={{ width: '100%' }}>
                <tbody>
                  {byCategory[cat]!.map(subject => (
                    <tr key={subject.id}>
                      <td>
                        {editingId === subject.id ? (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <input autoFocus value={editName} onChange={e => setEditName(e.target.value)}
                              onKeyDown={e => { if (e.key === 'Enter') handleUpdate(subject.id); if (e.key === 'Escape') setEditingId(null); }}
                              className="input" style={{ flex: 1, height: 32, fontSize: 13 }} />
                            <input value={editCode} onChange={e => setEditCode(e.target.value)}
                              placeholder="Code (opt.)" className="input" style={{ width: 90, height: 32, fontSize: 13 }} />
                            <button onClick={() => handleUpdate(subject.id)} disabled={editSaving} className="icon-btn" style={{ color: 'var(--success)' }}>
                              {editSaving ? <Loader2 size={14} style={{ animation: 'pa-spin .7s linear infinite' }} /> : <Check size={14} />}
                            </button>
                            <button onClick={() => setEditingId(null)} className="icon-btn"><X size={14} /></button>
                          </div>
                        ) : (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <span style={{ fontSize: 13.5, fontWeight: 600 }}>{subject.name}</span>
                            {subject.code && (
                              <span style={{ fontSize: 11, fontFamily: 'monospace', color: 'var(--text-3)', background: 'var(--surface-2)', padding: '1px 6px', borderRadius: 4 }}>
                                {subject.code}
                              </span>
                            )}
                          </div>
                        )}
                      </td>
                      {editingId !== subject.id && (
                        <td style={{ width: 80, textAlign: 'right' }}>
                          <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
                            <button onClick={() => { setEditingId(subject.id); setEditName(subject.name); setEditCode(subject.code || ''); }}
                              className="icon-btn" title="Rename"><Pencil size={13} /></button>
                            <button onClick={() => handleDelete(subject)} className="icon-btn" title="Remove" style={{ color: 'var(--danger)' }}>
                              <Trash2 size={13} />
                            </button>
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </div>
      )}

      {/* Catalog Modal */}
      {showCatalog && (
        <>
          <div className="scrim" onClick={closeCatalog} />
          <div className="modal" style={{ width: 700, maxHeight: '82vh', display: 'flex', flexDirection: 'column' }}>
            <div className="modal-head">
              <div style={{ flex: 1 }}>
                <h2>Subject Catalog</h2>
                <p>Select subjects to add to your school library</p>
              </div>
              <button className="icon-btn" onClick={closeCatalog}><X size={20} /></button>
            </div>

            {/* Search + grade group tabs */}
            <div style={{ padding: '12px 24px 0', borderBottom: '1px solid var(--divider)', flexShrink: 0 }}>
              <div style={{ position: 'relative', marginBottom: 12 }}>
                <Search size={14} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-3)', pointerEvents: 'none' }} />
                <input autoFocus value={catalogSearch} onChange={e => setCatalogSearch(e.target.value)}
                  placeholder="Search subjects…" className="input" style={{ paddingLeft: 36, width: '100%' }} />
              </div>
              <div className="tabs" style={{ borderBottom: 'none' }}>
                {(['all', 'preschool', 'primary', 'middle', 'secondary', 'senior', 'vocational'] as const).map(g => (
                  <button key={g} onClick={() => setCatalogGroup(g)} className={catalogGroup === g ? 'active' : ''} style={{ fontSize: 12 }}>
                    {g === 'all' ? 'All' : GRADE_GROUP_LABELS[g]}
                  </button>
                ))}
              </div>
            </div>

            {/* Chips grid */}
            <div className="modal-body" style={{ flex: 1, overflowY: 'auto' }}>
              {catalogFiltered.length === 0 ? (
                <div className="empty" style={{ padding: '32px 0' }}><p>No subjects match your search</p></div>
              ) : (
                (Object.entries(catalogGrouped) as [SubjectCategory, typeof SUBJECT_CATALOG][]).map(([cat, entries]) => (
                  <div key={cat} style={{ marginBottom: 22 }}>
                    <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: 0.6, textTransform: 'uppercase', color: 'var(--text-3)', marginBottom: 10 }}>
                      {CATEGORY_LABELS[cat]}
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                      {entries.map(entry => {
                        const alreadyAdded = existingNames.has(entry.name.toLowerCase());
                        const isSelected = catalogSelected.has(entry.name);
                        return (
                          <button key={entry.name} disabled={alreadyAdded}
                            onClick={() => {
                              if (alreadyAdded) return;
                              setCatalogSelected(prev => {
                                const n = new Set(prev);
                                if (n.has(entry.name)) n.delete(entry.name); else n.add(entry.name);
                                return n;
                              });
                            }}
                            className={`chip ${CATEGORY_CAT_CLASS[entry.category]}`}
                            style={{
                              cursor: alreadyAdded ? 'default' : 'pointer',
                              opacity: alreadyAdded ? 0.45 : 1,
                              border: isSelected ? '2px solid var(--accent-deep)' : '2px solid transparent',
                              background: isSelected ? 'var(--accent-wash)' : undefined,
                              color: isSelected ? 'var(--accent-deep)' : undefined,
                              display: 'inline-flex', alignItems: 'center', gap: 5,
                            }}>
                            {(isSelected || alreadyAdded) && <Check size={11} />}
                            {entry.name}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))
              )}
            </div>

            <div className="modal-foot">
              <span className="muted" style={{ fontSize: 12, flex: 1 }}>
                {catalogSelected.size > 0
                  ? `${catalogSelected.size} subject${catalogSelected.size !== 1 ? 's' : ''} selected`
                  : 'Click subjects to select · greyed = already in library'}
              </span>
              <button className="btn btn-ghost" onClick={closeCatalog}>Cancel</button>
              <button className="btn btn-primary" disabled={catalogSelected.size === 0 || saving} onClick={handleAddFromCatalog}>
                {saving
                  ? <><Loader2 size={14} style={{ animation: 'pa-spin .7s linear infinite' }} /> Adding…</>
                  : <><Plus size={14} /> Add {catalogSelected.size > 0 ? catalogSelected.size : ''} Subject{catalogSelected.size !== 1 ? 's' : ''}</>
                }
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};
